import React, { useEffect, useRef, useState } from "react";
import Modal from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/button";
import { Field, Input, Select } from "../../../components/ui/Field";
import { useApiMutation } from "../../../hooks/useApiMutation";
import { useApiQuery } from "../../../hooks/useApiQuery";
import { batchApi } from "../../../api/batchApi";
import { batchMasterApi } from "../../../api/batchMasterApi";
import { useToast } from "../../../components/ui/toast/ToastProvider";

const DispatchBatchModal = ({ open, batch, onClose, onDispatched }) => {
  const toast = useToast();
  const [dispatchTo, setDispatchTo] = useState("");
  const [validationError, setValidationError] = useState("");
  const submitting = useRef(false);
  const { run: dispatchBatch, isLoading, error, reset } = useApiMutation(batchApi.dispatch);
  const { data: oems, isFetching: loadingOems, error: oemError, refetch } = useApiQuery(
    () => batchMasterApi.oems(), [open], { skip: !open }
  );

  useEffect(() => {
    setDispatchTo("");
    setValidationError("");
    reset();
  }, [batch, open, reset]);

  const handleClose = () => { if (!submitting.current) onClose(); };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current || batch?.dispatch_on) return;
    if (!(oems || []).some((oem) => oem.oem_id === dispatchTo)) {
      setValidationError("Select an OEM / customer.");
      return;
    }
    const qrValue = batch?.qr_value || (batch?.plant_id && batch?.batch_id ? `${batch.plant_id}_${batch.batch_id}` : "");
    if (!qrValue) {
      setValidationError("This batch does not have a valid QR code.");
      return;
    }
    submitting.current = true;
    try {
      const result = await dispatchBatch({ qr_value: qrValue, dispatch_to: dispatchTo });
      toast.success(`Batch ${batch.batch_id} dispatched successfully.`);
      onDispatched?.(result);
      onClose();
    } catch (err) {
      toast.error(err?.message || "Unable to dispatch batch.");
    } finally {
      submitting.current = false;
    }
  };

  if (!batch) return null;
  const ready = !batch.dispatch_on && (!batch.started_at || ["COMPLETED", "DISPATCHED"].includes(String(batch.status).toUpperCase()));

  return (
    <Modal open={open} onClose={handleClose} title="Dispatch Batch"
      description={`${batch.batch_id || batch.uid || batch.id} will be marked as dispatched.`} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Dispatch To" required error={validationError}>
          <Select aria-label="Dispatch To" value={dispatchTo} disabled={isLoading || loadingOems}
            onChange={(event) => { setDispatchTo(event.target.value); setValidationError(""); }}>
            <option value="">{loadingOems ? "Loading customers…" : "Select OEM / customer…"}</option>
            {(oems || []).map((oem) => <option key={oem.oem_id} value={oem.oem_id}>{oem.oem_name || oem.oem_id}</option>)}
          </Select>
        </Field>
        <Field label="Batch Quantity"><Input aria-label="Batch Quantity" value={batch.batch_quantity ?? batch.quantity ?? ""} readOnly /></Field>
        {!ready && <p role="alert" className="text-sm text-amber-600">Complete this batch before dispatching it.</p>}
        {oemError && <p role="alert" className="text-sm text-rose-600">Unable to load customers. <button type="button" onClick={refetch} className="underline">Retry</button></p>}
        {error && <p role="alert" className="text-sm text-rose-600">{error.message}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" disabled={isLoading || loadingOems || Boolean(oemError) || !ready}>{isLoading ? "Dispatching…" : "Dispatch Batch"}</Button>
        </div>
      </form>
    </Modal>
  );
};

export default DispatchBatchModal;
